import get from 'lodash/get'
import mergeWith from 'lodash/mergeWith'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'
import without from 'lodash/without'
import type { BusinessData, CustomFieldsConfig } from 'types/business'
import type { Group } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { Track } from 'types/track'
import type { LoginBusiness, UserSettings } from 'types/user'
import type { HttpErrorType } from 'types/errors'
import {
  ADD_TO_DISCO,
  ADD_TO_DISCO_CHOOSE_BUSINESS,
  ADD_TO_DISCO_ERROR,
  BUSINESS_DATA_LOAD,
  BUSINESS_SETTINGS_LOAD,
  BUSINESS_SETTINGS_UPDATE,
  BUSINESS_SETTINGS_UPDATE_FAIL,
  BUSINESS_SETTINGS_UPDATE_PROGRESS,
  BUSINESS_SETTINGS_UPDATE_SUCCESS,
  BUSINESS_USERS_LOAD_SUCCESS,
  GROUPS_LOAD_SUCCESS,
  PLAN_UPDATE_LOAD,
  PLAYLIST_LOAD_BATCH,
  START_SAVING_STATE,
  TRACK_DELETE_BATCH_SUCCESS,
  TRACK_LOAD_BATCH,
  USER_AUTOLOGIN,
  USER_AUTOLOGIN_ERROR,
  USER_AUTOLOGIN_SUCCESS,
  USER_BUSINESS_LIST_RESET,
  USER_CLEAR_MFA_STATE,
  USER_LOGIN,
  USER_LOGIN_ERROR_CLEAR,
  USER_LOGIN_FAIL,
  USER_LOGIN_REQUIRE_BUSINESS_SELECT,
  USER_LOGIN_REQUIRE_MFA,
  USER_LOGIN_SUCCESS,
  USER_PASSWORD_CHANGE,
  USER_PASSWORD_CHANGE_FAIL,
  USER_PASSWORD_CHANGE_SUCCESS,
  USER_PRELOADED,
  USER_SETTINGS_UPDATE,
  USER_SETTINGS_UPDATE_FAIL,
  USER_SETTINGS_UPDATE_SUCCESS,
  USER_URL_CHECK,
  USER_URL_CHECK_FAIL,
  USER_URL_CHECK_SUCCESS,
  USER_UPDATED_MOBILE_BUSINESS_LIST,
} from '@disco/constants/ActionTypes'
import { TERRITORY } from '@disco/constants/GroupTypes'
import type { JobsType } from '@disco/constants/user-jobs'

const processTracksUpdate = (state, tracks: Track[]): UserReducer => {
  const extraMetadata = get(
    state,
    'business.public_metadata.extra_metadata',
    null,
  )
  if (!extraMetadata) return state
  const possiblyAffectedKeys = Object.keys(extraMetadata)
    .map(key => ({ ...extraMetadata[key], key }))
    .filter(
      metaKey =>
        (metaKey.type === 'dropdown_single' ||
          metaKey.type === 'dropdown_multi') &&
        metaKey.allow_create,
    )
  if (possiblyAffectedKeys.length === 0) return state
  const newExtraMetadata: CustomFieldsConfig = tracks.reduce(
    (acc: CustomFieldsConfig, track: Track) => {
      let result = acc
      possiblyAffectedKeys.forEach(({ key }) => {
        if (!track.metadata) return
        // key comes from Object.keys(extraMetadata), so acc[key] is defined
        const newValues = track.metadata[key]
          ? without(track.metadata[key], ...(acc[key]!.values || []))
          : []

        if (newValues.length > 0) {
          result = {
            ...acc,
            [key]: {
              ...acc[key]!,
              values: [...(acc[key]!.values || []), ...newValues],
            },
          }
        }
      })
      return result
    },
    extraMetadata,
  )
  if (!state.business || !state.business.private_metadata) return state
  return {
    ...state,
    business: {
      ...state.business,
      private_metadata: {
        ...state.business.private_metadata,
        extra_metadata: newExtraMetadata,
      },
    },
  }
}

const initialState = {
  id: null,
  first_name: '',
  last_name: '',
  email: '',
  csrfToken: '',
  loggingIn: false,
  autologgingIn: false,
  loggedIn: false,
  loggedInWithMFA: false,
  loggedInWithSAML: false,
  preloaded: false,
  is_active: false,
  is_support_user: false,
  is_admin: false,
  autoSave: false,
  addToDiscoLoginDetails: null,
  groups: [],
  businessUpdating: false,
  businessImageUploadProgress: 0,
  updating: false,
  updated: false,
  libraries: [],
  territories: [],
  settings: {},
  stats: {},
  business: null,
  error: null,
  mfaCodeRequired: false,
  mfaType: '',
  constance: {},
  showLoginError: false,
  passwordChanging: false,
  passwordChanged: false,
  passwordChangeError: null,
  loginUrlChecking: false,
  loginUrlChecked: false,
  loginUrlCheckError: null,
  userBusinesses: [],
  // userBusinesses are cleared after login, mobile app need access to user businesses
  // so user can switch business in app without logging out, we'll store businesses
  // in separate prop for now to evade changing any logic on frontend
  mobileBusinesses: [],
  business_user_settings: {
    job: null,
  },
  planUpdateLoad: false,
}
export type UserReducer = {
  id: number | null | undefined
  first_name: string
  last_name: string
  email: string
  csrfToken: string
  loggingIn: boolean
  autologgingIn: boolean
  loggedIn: boolean
  loggedInWithMFA: boolean
  loggedInWithSAML: boolean
  preloaded: boolean
  is_active: boolean
  // part of invite flow, `false` if invite wasn't accept
  is_support_user: boolean
  is_admin: boolean
  autoSave: boolean
  addToDiscoLoginDetails: Record<string, any> | null | undefined
  groups: Group[]
  businessUpdating: boolean
  businessImageUploadProgress: number
  updating: boolean
  updated: boolean
  libraries: number[]
  territories: number[]
  settings: UserSettings
  stats: Record<string, any>
  business: BusinessData | null | undefined
  error: HttpErrorType | null | undefined
  permissions?: string[]
  mfaCodeRequired: boolean
  mfaType?: string
  constance: Record<string, any>
  showLoginError: boolean
  passwordChanging: boolean
  passwordChanged: boolean
  passwordChangeError: Error | null | undefined
  loginUrlChecking: boolean
  loginUrlChecked: boolean
  loginUrlCheckError: HttpErrorType | null | undefined
  userBusinesses: LoginBusiness[]
  mobileBusinesses: LoginBusiness[]
  business_user_settings: {
    job: JobsType
  }
  planUpdateLoad: boolean
}

const mergeCustomizer = (_objValue, srcValue, key) => {
  // all arrays should be rewritten
  if (Array.isArray(srcValue)) {
    return srcValue
  }

  // rewrite entityMap to avoid keeping removed entities
  if (key === 'entityMap') {
    return srcValue
  }
}

export function userReducer(
  state: UserReducer = initialState,
  action: any,
): UserReducer {
  const { result, type, error, mfaUsed, loginType, mfaType } = action

  switch (type) {
    case BUSINESS_DATA_LOAD:
      return { ...state, business: result }

    case BUSINESS_USERS_LOAD_SUCCESS: {
      // business is loaded before user count actions
      if (!state.business) return state
      return {
        ...state,
        business: {
          ...state.business,
          stats: {
            ...state.business.stats,
            number_of_users: action.businessUsers.length,
          },
        },
      }
    }

    case TRACK_DELETE_BATCH_SUCCESS: {
      const numDeletedTracks = action.trackIds.length
      const currentTrackCount = state.business?.stats.total_track_count || 0
      const newTracksCount = Math.max(currentTrackCount - numDeletedTracks, 0)
      if (!state.business) return state
      return {
        ...state,
        business: {
          ...state.business,
          stats: {
            // business is loaded before track deletion actions
            ...state.business.stats,
            total_track_count: newTracksCount,
          },
        },
      }
    }

    case BUSINESS_SETTINGS_LOAD:
      // @ts-expect-error - todo fix later
      return { ...state, businessSettings: result }

    case USER_URL_CHECK:
      return {
        ...state,
        loginUrlChecking: true,
        loginUrlChecked: false,
        loginUrlCheckError: null,
      }

    case USER_URL_CHECK_SUCCESS:
      return {
        ...state,
        loginUrlChecking: false,
        loginUrlChecked: true,
        loginUrlCheckError: null,
      }

    case USER_URL_CHECK_FAIL:
      return {
        ...state,
        loginUrlChecking: false,
        loginUrlChecked: false,
        loginUrlCheckError: error,
      }

    case USER_AUTOLOGIN:
      return { ...state, autologgingIn: true }

    case USER_AUTOLOGIN_ERROR:
      return { ...state, autologgingIn: false }

    case USER_LOGIN:
      return { ...state, loggingIn: true, showLoginError: false }

    case USER_LOGIN_REQUIRE_MFA:
      return {
        ...state,
        error: null,
        mfaCodeRequired: true,
        mfaType: mfaType,
        loggingIn: false,
        loggedIn: false,
      }

    case USER_AUTOLOGIN_SUCCESS:
    case USER_LOGIN_SUCCESS:
      if (result) {
        return {
          ...state,
          loggingIn: false,
          loggedIn: true,
          error: null,
          // clean up
          addToDiscoLoginDetails: null,
          mfaCodeRequired: false,
          loggedInWithMFA: mfaUsed,
          loggedInWithSAML: loginType === 'saml',
          userBusinesses: [],
          ...result,
        }
      }

      return state

    case USER_PRELOADED:
      return { ...state, preloaded: true }

    case USER_CLEAR_MFA_STATE:
      return { ...state, mfaCodeRequired: false }

    case USER_LOGIN_FAIL:
      return {
        ...state,
        loggingIn: false,
        loggedIn: false,
        showLoginError: true,
        error: error,
      }

    case USER_LOGIN_ERROR_CLEAR:
      return { ...state, showLoginError: false, error: null }

    case USER_SETTINGS_UPDATE:
      return { ...state, updating: true }

    case USER_SETTINGS_UPDATE_SUCCESS:
      return mergeWith(
        {},
        { ...state, updating: false, updated: true, loggedIn: true },
        result,
        mergeCustomizer,
      )

    case USER_SETTINGS_UPDATE_FAIL:
      return mergeWith(
        {},
        { ...state, updating: false, updated: false, error },
        result,
        mergeCustomizer,
      )

    case BUSINESS_SETTINGS_UPDATE:
      return {
        ...state,
        businessUpdating: true,
        businessImageUploadProgress: 0,
      }

    case BUSINESS_SETTINGS_UPDATE_PROGRESS:
      return { ...state, businessImageUploadProgress: action.progress }

    case BUSINESS_SETTINGS_UPDATE_SUCCESS:
      return {
        ...state,
        ...result,
        businessUpdating: false,
        businessImageUploadProgress: 0,
        planUpdateLoad: false,
      }

    case BUSINESS_SETTINGS_UPDATE_FAIL:
      return { ...state, businessUpdating: false, planUpdateLoad: false, error }

    case PLAN_UPDATE_LOAD:
      return { ...state, planUpdateLoad: true }

    case START_SAVING_STATE:
      return { ...state, autoSave: true }

    case USER_PASSWORD_CHANGE:
      return {
        ...state,
        passwordChanging: true,
        passwordChanged: false,
        passwordChangeError: null,
      }

    case USER_PASSWORD_CHANGE_FAIL:
      return {
        ...state,
        passwordChanging: false,
        passwordChanged: false,
        passwordChangeError: error,
      }

    case USER_PASSWORD_CHANGE_SUCCESS:
      return {
        ...state,
        passwordChanging: false,
        passwordChanged: true,
        passwordChangeError: null,
      }

    case ADD_TO_DISCO: {
      return { ...state, loggingIn: true, showLoginError: false }
    }

    case ADD_TO_DISCO_CHOOSE_BUSINESS: {
      return {
        ...state,
        loggingIn: false,
        error: null,
        // store password to pass it to auth
        addToDiscoLoginDetails: action.loginDetails,
        showLoginError: false,
      }
    }

    case ADD_TO_DISCO_ERROR: {
      return {
        ...state,
        loggingIn: false,
        showLoginError: action.error ? action.error.providedLoginDetails : true,
        error: error,
      }
    }

    case GROUPS_LOAD_SUCCESS: {
      const {
        groups,
        refresh,
      }: {
        groups: Group[]
        refresh: boolean
      } = action
      const territories = groups
        .filter(g => g.group_type === TERRITORY)
        .map(g => g.id)
      return {
        ...state,
        territories: refresh
          ? uniq([...state.territories, ...territories])
          : territories,
      }
    }

    case TRACK_LOAD_BATCH: {
      // exit early if we don't have anything to do
      if (!action.list || action.list.length === 0) return state
      return processTracksUpdate(state, action.list)
    }

    case PLAYLIST_LOAD_BATCH: {
      const tracks: Track[] = action.list
        .filter(
          (playlist: Playlist) => playlist.tracks && playlist.tracks.length > 0,
        )
        .reduce((acc: Track[], playlist: Playlist) => {
          return [...acc, ...playlist.tracks]
        }, [])
      if (tracks.length === 0) return state
      const uniqueTracks: Track[] = uniqBy(tracks, 'id')
      return processTracksUpdate(state, uniqueTracks)
    }

    case USER_LOGIN_REQUIRE_BUSINESS_SELECT:
      return {
        ...state,
        userBusinesses: action.businesses,
        mobileBusinesses: action.businesses,
      }

    case USER_BUSINESS_LIST_RESET:
      return {
        ...state,
        userBusinesses: [],
        mobileBusinesses: [],
        loggingIn: false,
        mfaCodeRequired: false,
      }

    case USER_UPDATED_MOBILE_BUSINESS_LIST:
      return {
        ...state,
        mobileBusinesses: action.businesses,
      }

    default:
      return state
  }
}
